import React, { useState } from 'react';
import { useLocation, useNavigate, type Location } from 'react-router-dom';
import { authApi } from '../../services/api/auth.api';
import { getApiErrorCode, getApiErrorMessage } from '../../utils/apiError';

const DEFAULT_POST_LOGIN_PATH = '/admin/sessions';

const getPostLoginPath = (from: Location | null | undefined): string => {
  if (!from?.pathname?.startsWith('/admin') || from.pathname === '/admin/login') {
    return DEFAULT_POST_LOGIN_PATH;
  }
  return `${from.pathname}${from.search}${from.hash}`;
};

export const AdminLogin: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectFrom = (location.state as { from?: Location } | null)?.from;
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await authApi.login(login, password);
      localStorage.setItem('teamlab_admin_token', result.token);
      navigate(getPostLoginPath(redirectFrom), { replace: true });
    } catch (err) {
      setError(
        getApiErrorCode(err) === 'ACCOUNT_PENDING'
          ? getApiErrorMessage(err, 'Compte pas encore activé : utilisez le lien d’accès envoyé par votre administrateur.')
          : 'Identifiants invalides'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none"></div>

      <div className="relative z-10 w-full max-w-md rounded-3xl border border-slate-200 bg-white/90 backdrop-blur-xl shadow-xl shadow-slate-200/60 p-8">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-lg">
            <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 11c0 .552-.448 1-1 1s-1-.448-1-1 .448-1 1-1 1 .448 1 1zm0 0V8m0 8h.01M5 20h14a2 2 0 002-2V10a2 2 0 00-.586-1.414l-5-5A2 2 0 0014 3H5a2 2 0 00-2 2v13a2 2 0 002 2z" />
            </svg>
          </div>
          <h1 className="text-3xl font-black tracking-tight text-slate-900">Admin TeamLab</h1>
          <p className="mt-2 text-sm text-slate-500">Connexion requise pour acceder au back-office.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="mb-2 block text-sm font-bold text-slate-700">Email ou identifiant</label>
            <input
              type="text"
              value={login}
              onChange={(e) => setLogin(e.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-slate-400"
              placeholder="prenom.nom@exemple.com"
              required
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-bold text-slate-700">Mot de passe</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-slate-400"
              placeholder="Votre mot de passe"
              required
            />
          </div>

          {error && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-600">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-2xl bg-slate-900 px-4 py-3 text-sm font-bold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? 'Connexion...' : 'Se connecter'}
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-slate-400">
          Première connexion ou mot de passe oublié ? Utilisez le lien envoyé par votre administrateur, ou demandez-lui-en un nouveau.
        </p>
      </div>
    </div>
  );
};
